import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Alert } from "@mui/material";
import * as React from "react";

import { basename } from "@/shared/domain/files/filesystem";
import { isDocumentViewable } from "@/shared/domain/files/fileTypes";
import { useDatasources } from "@/shared/state/visualization/hooks";

import { Expandable } from "../../Expandable";
import { FileSelectorSkeleton } from "../../FileSelector";
import { Label } from "../../Label";
import {
  SidebarTab,
  SidebarViewType,
  useWorkspaceSidebar,
} from "../../sidebarContext";
import { KeywordFilter } from "../../Topics/filter";
import { FilterInput } from "../../Topics/FilterInput";
import {
  useEphemeralWorkspaceState,
  useEphemeralWorkspaceStateLoading,
  useWorkspaceFiles,
} from "../../WorkspaceCtx/hooks";

import styles from "./FileList.module.css";

/**
 * Lists all workspace files grouped by type (topic-laden, document-viewable, other).
 * Actionable files (topic-laden or document-viewable) get a chevron that navigates to the Topics tab.
 */
export function FileList() {
  const files = useWorkspaceFiles();
  const { topics } = useEphemeralWorkspaceState();
  const isLoading = useEphemeralWorkspaceStateLoading();
  const sidebar = useWorkspaceSidebar();
  const datasources = useDatasources();

  // Estimate the number of skeleton rows to render while files are loading.
  // Prefer the resolved file count; otherwise use the count of file datasources;
  // otherwise assume a dataset with many files.
  const estimatedFileCount = React.useMemo(() => {
    if (files.length > 0) {
      return files.length;
    }
    const fileDatasources = datasources.filter((ds) => ds.type === "file");
    return fileDatasources.length > 0 ? fileDatasources.length : 10;
  }, [files.length, datasources]);

  const [filterTerm, setFilterTerm] = React.useState("");

  const handleFilterChange = React.useCallback((filter: KeywordFilter) => {
    setFilterTerm(filter.term);
  }, []);

  const keyRef = React.useRef(0);

  const navigateToTopics = React.useCallback(
    (fileId: string) => {
      keyRef.current += 1;

      sidebar.openView({
        type: SidebarViewType.TabbedSidebar,
        data: {
          initialSelectedFileId: {
            fileId,
            key: keyRef.current,
          },
        },
      });
      sidebar.setActiveTab(SidebarTab.Topics);
    },
    [sidebar],
  );

  const fileIdsWithTopics = React.useMemo(() => {
    return new Set(topics.map((t) => t.association.association_id));
  }, [topics]);

  const { topicFiles, documentFiles, otherFiles } = React.useMemo(() => {
    const topic: typeof files = [];
    const document: typeof files = [];
    const other: typeof files = [];
    const lowerFilter = filterTerm.toLowerCase();

    for (const file of files) {
      const name = file.relative_path
        ? basename(file.relative_path)
        : file.file_id;

      if (lowerFilter && !name.toLowerCase().includes(lowerFilter)) {
        continue;
      }

      if (fileIdsWithTopics.has(file.file_id)) {
        topic.push(file);
      } else if (isDocumentViewable(name)) {
        document.push(file);
      } else {
        other.push(file);
      }
    }

    return { topicFiles: topic, documentFiles: document, otherFiles: other };
  }, [files, fileIdsWithTopics, filterTerm]);

  if (isLoading) {
    return (
      <FileSelectorSkeleton
        className={styles["skeleton-container"]}
        estimatedFileCount={estimatedFileCount}
      />
    );
  }

  if (files.length === 0) {
    return (
      <div className={styles.container}>
        <Alert severity="info" className={styles["empty-alert"]}>
          No files in this workspace.
        </Alert>
      </div>
    );
  }

  const groups = [topicFiles, documentFiles, otherFiles].filter(
    (g) => g.length > 0,
  );

  return (
    <div className={styles.container}>
      <FilterInput
        className={styles.filter}
        placeholder="Filter files by name"
        onFilterChange={handleFilterChange}
      />
      <div className={styles.content}>
        {groups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && <hr className={styles.separator} />}
            {group.map((file) => {
              const name = file.relative_path
                ? basename(file.relative_path)
                : file.file_id;
              const hasTopics = fileIdsWithTopics.has(file.file_id);
              const actionable = hasTopics || isDocumentViewable(name);

              return (
                <div key={file.file_id} className={styles["file-row"]}>
                  <Expandable
                    data={file.file_id}
                    className={styles["file-name"]}
                    onClick={actionable ? navigateToTopics : undefined}
                    EndIcon={actionable ? ChevronRightIcon : undefined}
                    disabled={!actionable}
                  >
                    <Label text={name} title={file.relative_path} />
                  </Expandable>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
